// src/utils/storageManager.ts
export type GameRecord = {
  date: string;
  score: number;
  averageYearsOff: number;
  completionDate: string;
  correctGuesses: number;
};

export const StorageManager = {
  // Get today's date as YYYY-MM-DD
  getTodayDate: () => {
    // This uses the user's local time converted to UTC date.
    return new Date().toISOString().split('T')[0];
  },

  // Get today's date based on Eastern Time (America/New_York)
  getTodayDateET: () => {
    const now = new Date();
    // Format the date using options for America/New_York timezone
    // This ensures we get the correct YYYY-MM-DD regardless of the user's local timezone
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/New_York',
    };
    const formatter = new Intl.DateTimeFormat('en-CA', options); // 'en-CA' gives YYYY-MM-DD format
    // The formatter gives YYYY-MM-DD, which is what we need.
    return formatter.format(now);
  },

  // Keys
  KEYS: {
    ACTIVE_GAME: 'yearGuess_activeGame_',
    GAME_HISTORY: 'yearGuess_gameHistory',
    USER_STATS: 'yearGuess_userStats',
    COMPLETED_FLAG: 'yearGuess_completed_',
    CHALLENGE_RESULTS: 'yearGuess_challengeResults_',
    LAST_PLAYED: 'yearGuess_lastPlayed',
    RESULTS_DATA: 'yearGuess_results_',
  },

  // Save active game state
  saveActiveGame: (date: string, gameState: any) => {
    const key = StorageManager.KEYS.ACTIVE_GAME + date;
    localStorage.setItem(key, JSON.stringify(gameState));
  },

  // Load active game state
  loadActiveGame: (date: string) => {
    const key = StorageManager.KEYS.ACTIVE_GAME + date;
    const data = localStorage.getItem(key);
    if (!data) {
      return null;
    }
    try {
      const parsedData = JSON.parse(data);
      return parsedData;
    } catch (error) {
      return null;
    }
  },

  // Save completed game results
  saveGameResult: (
    date: string,
    result: { score: number; averageYearsOff: number },
    stats?: { correctGuesses?: number }
  ): void => {
    const history = StorageManager.getGameHistory();
    const existingIndex = history.findIndex(
      (item: GameRecord) => item.date === date
    );
    const completionDate = new Date().toISOString();
    const correctGuesses = stats?.correctGuesses ?? 0;

    if (existingIndex > -1) {
      history[existingIndex] = {
        ...history[existingIndex],
        score: result.score,
        averageYearsOff: result.averageYearsOff,
        correctGuesses: correctGuesses,
        completionDate: completionDate,
      };
    } else {
      history.push({
        date,
        score: result.score,
        averageYearsOff: result.averageYearsOff,
        correctGuesses: correctGuesses,
        completionDate: completionDate,
      });
      history.sort(
        (a: GameRecord, b: GameRecord) =>
          new Date(b.completionDate).getTime() -
          new Date(a.completionDate).getTime()
      );
    }

    localStorage.setItem(
      StorageManager.KEYS.GAME_HISTORY,
      JSON.stringify(history)
    );

    try {
      const updatedStats = StorageManager._calculateStatsFromHistory(history);
      localStorage.setItem(
        StorageManager.KEYS.USER_STATS,
        JSON.stringify(updatedStats)
      );
    } catch (statsError) {
      // Optionally clear the potentially stale cache on error
      // localStorage.removeItem(StorageManager.KEYS.USER_STATS);
    }
  },

  // Get game history (Safer Parsing)
  getGameHistory: (): GameRecord[] => {
    const historyJson = localStorage.getItem(StorageManager.KEYS.GAME_HISTORY);
    if (!historyJson) {
      return [];
    }
    try {
      const parsed = JSON.parse(historyJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  },

  // Update user statistics
  updateUserStats: (gameRecord: any) => {
    return StorageManager.getUserStats();
  },

  // Get user statistics
  getUserStats: () => {
    const history = StorageManager.getGameHistory();
    const calculatedStats = StorageManager._calculateStatsFromHistory(history);
    return calculatedStats;
  },

  // Check if a challenge is completed for a given date (REDUNDANT - Status checked via loadResultsData)
  /*
    isChallengeCompleted: (date: string) => {
      return localStorage.getItem(StorageManager.KEYS.COMPLETED_FLAG + date) === 'true';
    },
    */

  // Calculate stats from history (Enhanced Validation)
  _calculateStatsFromHistory: (history: GameRecord[]) => {
    const stats = {
      gamesPlayed: 0,
      averageScore: 0,
      highScore: 0,
      totalCorrectGuesses: 0,
      averageYearsOff: 0,
      lifetimePoints: 0,
    };

    if (!Array.isArray(history) || history.length === 0) {
      return stats;
    }

    let totalYearsOffSum = 0;
    let validGamesCount = 0;

    history.forEach((game, index) => {
      if (
        game &&
        typeof game.date === 'string' &&
        typeof game.score === 'number' &&
        !isNaN(game.score) &&
        typeof game.averageYearsOff === 'number' &&
        !isNaN(game.averageYearsOff)
      ) {
        validGamesCount++;
        stats.lifetimePoints += game.score;
        stats.highScore = Math.max(stats.highScore, game.score);
        stats.totalCorrectGuesses +=
          typeof game.correctGuesses === 'number' ? game.correctGuesses : 0;
        totalYearsOffSum += game.averageYearsOff;
      }
    });

    stats.gamesPlayed = validGamesCount;

    if (stats.gamesPlayed > 0) {
      stats.averageScore = Math.round(stats.lifetimePoints / stats.gamesPlayed);
      stats.averageYearsOff = totalYearsOffSum / stats.gamesPlayed;
    } else {
      stats.averageScore = 0;
      stats.averageYearsOff = 0;
      stats.lifetimePoints = 0;
      stats.highScore = 0;
      stats.totalCorrectGuesses = 0;
    }

    return stats;
  },

  deduplicateGameHistory: () => {
    try {
      const historyKey = StorageManager.KEYS.GAME_HISTORY;
      const statsKey = StorageManager.KEYS.USER_STATS;
      const historyJson = localStorage.getItem(historyKey);

      if (!historyJson) {
        const defaultStats = StorageManager._calculateStatsFromHistory([]);
        localStorage.setItem(statsKey, JSON.stringify(defaultStats));
        return;
      }

      let originalHistory: GameRecord[] = [];
      try {
        originalHistory = JSON.parse(historyJson);
        if (!Array.isArray(originalHistory))
          throw new Error('History is not an array');
      } catch (parseError) {
        localStorage.removeItem(historyKey); // Remove corrupted data
        const defaultStats = StorageManager._calculateStatsFromHistory([]);
        localStorage.setItem(statsKey, JSON.stringify(defaultStats));
        return;
      }

      const deduplicatedHistory: GameRecord[] = [];
      const processedDates = new Set<string>();
      let duplicatesFound = false;

      // Iterate backwards to keep the latest entry for each date
      for (let i = originalHistory.length - 1; i >= 0; i--) {
        const entry = originalHistory[i];
        // Basic validation of entry structure
        if (
          entry &&
          typeof entry.date === 'string' &&
          typeof entry.score === 'number' &&
          typeof entry.averageYearsOff === 'number'
        ) {
          if (!processedDates.has(entry.date)) {
            deduplicatedHistory.unshift(entry); // Add to beginning
            processedDates.add(entry.date);
          } else {
            duplicatesFound = true;
          }
        }
      }

      // Only save and recalculate if duplicates were found or history length changed
      if (
        duplicatesFound ||
        deduplicatedHistory.length !== originalHistory.length
      ) {
        localStorage.setItem(historyKey, JSON.stringify(deduplicatedHistory));

        // Recalculate stats based on deduplicated history
        const newStats =
          StorageManager._calculateStatsFromHistory(deduplicatedHistory);
        localStorage.setItem(statsKey, JSON.stringify(newStats));
      }
    } catch (error) {
      // console.error("[StorageManager] Error during deduplication:", error);
    }
  },

  // Save results data
  saveResultsData: (date: string, resultsData: any) => {
    const key = StorageManager.KEYS.RESULTS_DATA + date;
    try {
      const jsonData = JSON.stringify(resultsData);
      localStorage.setItem(key, jsonData);
      // Add verification log
      const verification = localStorage.getItem(key);
      if (verification === jsonData) {
        // console.log(`[StorageManager saveResultsData] Successfully saved and verified data for key: ${key}. Length: ${jsonData.length}`);
      } else {
        // console.error(`[StorageManager saveResultsData] SAVE VERIFICATION FAILED for key: ${key}! Data mismatch after setItem.`);
        // console.error(`[StorageManager saveResultsData] Expected length: ${jsonData.length}, Actual retrieved length: ${verification?.length}`);
      }
    } catch (error) {
      // console.error(`[StorageManager saveResultsData] Failed to save results data for key ${key}:`, error);
      // Log the data structure on error
      // console.error(`[StorageManager saveResultsData] Data structure that failed:`, {
      //     score: resultsData?.score,
      //     avgYearsOff: resultsData?.averageYearsOff,
      //     roundDataLength: resultsData?.roundData?.length,
      //     imageUrlsLength: resultsData?.imageUrls?.length,
      //     distributionStatsAvailable: !!resultsData?.distributionStats,
      //     processedDistributionAvailable: !!resultsData?.distributionStats?.processedDistribution,
      //     curvePointsAvailable: !!resultsData?.distributionStats?.processedDistribution?.curvePoints?.length
      // });
    }
  },

  // Load results data
  loadResultsData: (date: string) => {
    const key = StorageManager.KEYS.RESULTS_DATA + date;
    const data = localStorage.getItem(key);
    if (!data) {
      return null;
    }
    try {
      const parsedData = JSON.parse(data);
      return parsedData;
    } catch (error) {
      return null;
    }
  },
};

export default StorageManager;
